// Shared in-call control bar for the caller (CallView) and the staff softphone
// (StaffCallView). Uses the universally-recognized video-call icons (microphone /
// camera / flip / red phone-down), with NO visible text — so the controls are
// language-agnostic (contact-center agents in any language understand them, same as
// Zoom/Meet/WhatsApp). On/off state is shown by a slashed icon on a red button.
// aria-label is kept for screen readers only (localised via the i18n dictionary).
import type { ReactNode } from 'react'
import { t } from '../i18n'

const svg = (children: ReactNode) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
)

const MicOn = svg(<><path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" /><path d="M19 11v1a7 7 0 0 1-14 0v-1" /><line x1="12" y1="19" x2="12" y2="22" /><line x1="8" y1="22" x2="16" y2="22" /></>)
const MicOff = svg(<><line x1="3" y1="3" x2="21" y2="21" /><path d="M9 9v2a3 3 0 0 0 4.83 2.39" /><path d="M15 11.5V5a3 3 0 0 0-5.92-.7" /><path d="M19 11v1a7 7 0 0 1-10.5 6.06M5 11v1a7 7 0 0 0 .2 1.66" /><line x1="12" y1="19" x2="12" y2="22" /><line x1="8" y1="22" x2="16" y2="22" /></>)
const CamOn = svg(<><rect x="2" y="6" width="13" height="12" rx="2" /><path d="M22 8.5 15 12l7 3.5v-7z" /></>)
const CamOff = svg(<><line x1="3" y1="3" x2="21" y2="21" /><path d="M15 11.5V8a2 2 0 0 0-2-2H7M4.2 6.2A2 2 0 0 0 2 8v8a2 2 0 0 0 2 2h9a2 2 0 0 0 1.8-1.1" /><path d="M22 8.5 15 12v0l7 3.5v-7z" /></>)
const Flip = svg(<><polyline points="21 4 21 9 16 9" /><polyline points="3 20 3 15 8 15" /><path d="M19.5 9A7.5 7.5 0 0 0 6.8 5.5L3 9m18 6-3.8 3.5A7.5 7.5 0 0 1 4.5 15" /></>)
const HangUp = (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true">
    <g transform="rotate(135 12 12)">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </g>
  </svg>
)

interface Props {
  micOn: boolean
  videoOn: boolean
  canSwitchCam: boolean
  visible: boolean
  onToggleMic: () => void
  onToggleVideo: () => void
  onSwitchCam: () => void
  onHangup: () => void
  poke: () => void
}

const CallControls = ({ micOn, videoOn, canSwitchCam, visible, onToggleMic, onToggleVideo, onSwitchCam, onHangup, poke }: Props) => {
  const tap = (fn: () => void, repoke = true) => (e: React.MouseEvent) => {
    e.stopPropagation()
    fn()
    if (repoke) poke()
  }
  return (
    <div className={`cf-controls ${visible ? '' : 'hidden'}`}>
      <button className={`cf-ctrl ${micOn ? '' : 'off'}`} onClick={tap(onToggleMic)}
              aria-label={micOn ? t.muteMic : t.unmuteMic}>
        {micOn ? MicOn : MicOff}
      </button>
      <button className={`cf-ctrl ${videoOn ? '' : 'off'}`} onClick={tap(onToggleVideo)}
              aria-label={videoOn ? t.turnOffCamera : t.turnOnCamera}>
        {videoOn ? CamOn : CamOff}
      </button>
      {canSwitchCam && (
        <button className="cf-ctrl" onClick={tap(onSwitchCam)} aria-label={t.switchCamera}>
          {Flip}
        </button>
      )}
      <button className="cf-ctrl hangup" onClick={tap(onHangup, false)} aria-label={t.hangUp}>
        {HangUp}
      </button>
    </div>
  )
}

export default CallControls
